import type { NoCredits, WorkspaceResult } from "~/protocol";
import { CREDITS_PER_SEAT } from "~/protocol/billing";

// What the top pane and the Files sheet say about an unlock (#97): its refusals, by the
// server's code, and the org's balance beside the buttons. The mapped ones are full sentences
// with contractions, like the billing page; the server's own refusals ("… is already
// unlocked") pass through as they are.

/** A refusal's words, and the label of its way out to /billing when the clicker has one. */
export interface Refusal {
  text: string;
  action?: "Buy credits" | "Subscribe";
}

const NO_CREDITS: Record<NoCredits, Refusal> = {
  admin: { text: "Your org has no credits left.", action: "Buy credits" },
  subscribe: {
    text: `Your org has no credits. Pro adds ${CREDITS_PER_SEAT} credits a month per seat.`,
    action: "Subscribe",
  },
  member: { text: "Your org has no credits left. Ask an org admin to add more." },
};

/** Out of credits with a top-up being charged: nothing to do but wait, whoever clicked. */
const TOP_UP_PENDING: Refusal = {
  text: "Your org has no credits left. More are on the way, so try again in a minute.",
};

const CODES: Record<string, string> = {
  busy: "Wait for the current run to finish",
  billing_off: "Removing the watermark isn't available right now.",
  // The inputs moved since the render, or it predates their record: it claims no cause, and
  // says why a render comes first.
  stale_render:
    "Ask the director to render this video again first, so the clean copy matches what you see.",
  // The socket dropped mid-unlock: the server finishes it, and a failure gives the credit back
  // (at once, or by the stale-hold sweep if the server died), so no promise of when.
  disconnected:
    "Lost the connection. Reload to see whether the watermark came off. An attempt that fails gives its credit back.",
};

/** The words for a refused unlock; null when it went through. */
export function unlockRefusal(r: WorkspaceResult): Refusal | null {
  if (r.ok) return null;
  const payload = r.payload?.kind === "unlock_video" ? r.payload : undefined;
  if (payload?.noCredits)
    return payload.topUpPending ? TOP_UP_PENDING : NO_CREDITS[payload.noCredits];
  if (!r.error) return { text: "Couldn't remove the watermark." };
  return { text: CODES[r.error] ?? r.error };
}

/** The org's balance beside the Unlock buttons. */
export const creditCount = (n: number): string => `${n} ${n === 1 ? "credit" : "credits"} left`;

/** What the balance is, on hover: the billing page's own sentence. */
export const CREDITS_TITLE = "Your org's credits. One credit removes the watermark from one video.";
